import emittable from "./emittable";
import {
    Object3D,
    TextureLoader,
    Geometry,
    PointsMaterial,
    Points,
    Vector3
} from "three";

import globeAlphaMap from '@assets/images/globe/alphamap.png'

const mapCoordinate = (i1, i2, w1, w2, p) => ((p - i1) / (i2 - i1)) * (w2 - w1) + w1;

// https://stackoverflow.com/questions/38772400/c-sharp-wrapping-a-2d-array-to-a-sphere

const getImageData = function (image) {

    const canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0);

    return ctx.getImageData(0, 0, image.width, image.height);
}

const O3D = emittable(Object3D);

export default emittable(
    class GlobeCloud extends Object3D {
        constructor(props={}) {
            super();

            this.container = new O3D();
            this.container.defaultRotationX = -1.5;
            this.add(this.container);

            Object.assign(this,props);

            new TextureLoader().load(globeAlphaMap, this.generateDots);
        }

        radius = 45
        resolution = 2

        generateDots = ({
            image
        }) => {
            const geometry = new Geometry();
            const material = new PointsMaterial({
                size: 1.5,
                color: 0x000000,
                sizeAttenuation: false
            });
            const imagedata = getImageData(image);
            const thetaMax = Math.PI * 2, thetaMin = 0, phiMax = Math.PI, phiMin = 0;
            const {radius,resolution} = this;
            for (let i = 0; i < image.width; i+=resolution * 2)
            {
                for (let j = 0; j < image.height; j+=resolution)
                {
                    if (imagedata.data[(i * 4 + j * 4 * imagedata.width)] < 128) continue;

                    // map the angles from image coordinates
                    // const verticalOffset = 1 - ((.5 - (j / image.height)) * 2);
                    const theta = mapCoordinate(0.0, image.width - 1, thetaMin, thetaMax, i);
                    const phi = mapCoordinate( 0.0, image.height - 1, phiMin, phiMax, j);
                    const x = radius * Math.sin(phi) * Math.cos(theta);
                    const y = radius * Math.sin(phi) * Math.sin(theta);
                    const z = radius * Math.cos(phi);

                    geometry.vertices.push(new Vector3(x,y,z));
                }
            }

            this.particles = new Points(geometry, material);
            this.particles.rotation.z = Math.PI;
            this.container.add(this.particles);
            this._updateState();
        }

        get markers(){
            return this._markers || [];
        }

        set markers(markers){
            if(this._markers){
                this._markers.forEach(marker=>this.container.remove(marker));
            }
            markers = markers || [];
            this._markers = [...markers];
            this._markers.forEach(marker=>this.container.add(marker));
            this._markersDirty = true;
            this._updateState();
        }

        get selectedIndex(){
            if(this._selectedIndex === undefined){
                this._selectedIndex = -1;
            }

            return this._selectedIndex;
        }

        set selectedIndex(index){
            this._selectedIndex = index;
            this._updateState();
        }

        _updateState(){
            const index = this._selectedIndex;
            this.targetRotationAngle = undefined;
            this.targetTiltAngle = undefined;
            this._markers.forEach((marker,i)=>{
                marker.active = i===index;
                if(i!==index) return;
                this.targetRotationAngle = this.getTargetRotationAngle(marker);
                this.targetTiltAngle = this.getTargetTiltAngle(marker);
            });
        }

        getTargetRotationAngle(target,offset=2.5){
            return Math.atan2(target.position.x, target.position.y) + offset;
        }

        getTargetTiltAngle(target,offset=-1.5,max=-.7,min=-2){
            const angle = Math.atan2(target.position.z,target.position.x) + offset;
            return Math.min(max,Math.max(min,angle));
        }

        tick(delta){
            if(this._markersDirty){
                this._markers.forEach(marker=>{
                    marker.radius = this.radius + .2;
                });
                this._markersDirty = false;
            }

            const {targetRotationAngle,targetTiltAngle,container:{rotation,defaultRotationX:defaultContainerRotationX}} = this;
            if(targetRotationAngle !== undefined){
                rotation.z += (targetRotationAngle - rotation.z) / 13;
                rotation.x += (targetTiltAngle - rotation.x) / 10;
            }else{
                rotation.z = (rotation.z + delta / 10000)%(Math.PI*2);
                rotation.x += (defaultContainerRotationX - rotation.x) / 10;
            }

        }
    }
)